import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { FileInput } from '~/components/ui/form-items/file-input'
import { FormInput } from '~/components/ui/form-items/input'
import { FormSelect } from '~/components/ui/form-items/select'
import { FormSwitch } from '~/components/ui/form-items/switch'
import { DEFAULT_SKILL_CATEGORY_ID, SKILL_CATEGORIES } from '~/constants/skill-category'
import { useMutation } from '~/hooks/use-mutation'
import { useQueryClientHelper } from '~/hooks/use-query-client'
import { useToast } from '~/hooks/use-toast'
import { type SkillFormData, skillSchema } from '~/schemas/skill'
import { useAppStore } from '~/stores/app'
import { DialogId } from '~/types/common'
import { MutationKey } from '~/types/mutation-key'
import { QueryKey } from '~/types/query-key'
import type { Skill } from '~/types/skill'

type SkillResponse = { skill: Skill }

// HER-323: mirror the server's MAX_FILE_SIZE (5 MB) so an oversized icon is
// rejected before the skill is created (create-then-upload would otherwise
// leave an iconless skill when the upload fails server-side).
const MAX_ICON_SIZE = 5 * 1024 * 1024

type SkillFormDialogProps = {
  editingItem?: Skill | null
}

export function SkillFormDialog({ editingItem }: SkillFormDialogProps) {
  const { t } = useTranslation()
  const { showSuccess } = useToast()
  const { invalidateQueries } = useQueryClientHelper()
  const openDialogIds = useAppStore((state) => state.openDialogIds)
  const closeDialog = useAppStore((state) => state.closeDialog)

  const isCreateOpen = openDialogIds.includes(DialogId.CREATE_SKILL)
  const isUpdateOpen = openDialogIds.includes(DialogId.UPDATE_SKILL)
  const isOpen = isCreateOpen || isUpdateOpen
  const isEditing = isUpdateOpen && !!editingItem
  const dialogId = isEditing ? DialogId.UPDATE_SKILL : DialogId.CREATE_SKILL

  const [iconFile, setIconFile] = useState<File | null>(null)

  // Stable object the UPLOAD_SKILL_ICON endpoint reads at call time. On create
  // the skill id is only known after the create resolves, so we mutate `.id`
  // just before firing the icon upload rather than re-instantiating the hook.
  const iconIdParams = useRef({ id: '' }).current

  const { control, handleSubmit, reset } = useForm<SkillFormData>({
    resolver: zodResolver(skillSchema),
    defaultValues: { nameEn: '', nameJa: '', categoryId: DEFAULT_SKILL_CATEGORY_ID, isActive: true },
  })

  const categoryOptions = SKILL_CATEGORIES.map((category) => ({
    value: category.id,
    label: t(`skillCategory.${category.slug}`),
  }))

  useEffect(() => {
    if (isEditing && editingItem) {
      reset({
        nameEn: editingItem.nameEn,
        nameJa: editingItem.nameJa,
        categoryId: editingItem.category?.id ?? editingItem.categoryId ?? DEFAULT_SKILL_CATEGORY_ID,
        isActive: editingItem.isActive,
      })
      setIconFile(null)
    } else if (isCreateOpen) {
      reset({ nameEn: '', nameJa: '', categoryId: DEFAULT_SKILL_CATEGORY_ID, isActive: true })
      setIconFile(null)
    }
  }, [isEditing, isCreateOpen, editingItem, reset])

  const finalize = () => {
    showSuccess(t('common.save'))
    invalidateQueries(QueryKey.SKILLS)
    setIconFile(null)
    closeDialog(dialogId)
  }

  const { mutate: uploadIcon, isPending: isUploadingIcon } = useMutation<SkillResponse, FormData>({
    mutationKey: MutationKey.UPLOAD_SKILL_ICON,
    method: 'POST',
    idParams: iconIdParams,
    options: { onSuccess: finalize },
  })

  // After the row is written, upload the staged icon (if any) before finalizing.
  const handleRowSaved = (skillId: string) => {
    if (iconFile) {
      iconIdParams.id = skillId
      const formData = new FormData()
      formData.append('file', iconFile)
      uploadIcon(formData)
    } else {
      finalize()
    }
  }

  const { mutate: createSkill, isPending: isCreating } = useMutation<SkillResponse, SkillFormData>({
    mutationKey: MutationKey.CREATE_SKILL,
    method: 'POST',
    options: { onSuccess: (data) => handleRowSaved(data.skill.id) },
  })

  const { mutate: updateSkill, isPending: isUpdating } = useMutation<SkillResponse, SkillFormData>({
    mutationKey: MutationKey.UPDATE_SKILL,
    method: 'PATCH',
    idParams: editingItem ? { id: editingItem.id } : undefined,
    options: { onSuccess: (data) => handleRowSaved(data.skill.id) },
  })

  const isPending = isCreating || isUpdating || isUploadingIcon

  const onSubmit = (formValues: SkillFormData) => {
    if (isEditing) {
      updateSkill(formValues)
    } else {
      createSkill(formValues)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog(dialogId)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? t('skills.editSkill') : t('skills.addNew')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormInput control={control} name="nameEn" label={t('skills.nameEn')} required />
          <FormInput control={control} name="nameJa" label={t('skills.nameJa')} required />
          <FormSelect
            control={control}
            name="categoryId"
            label={t('skills.category')}
            required
            options={categoryOptions}
            disabled={isPending}
          />
          <FileInput
            id="skill-icon"
            label={t('skills.icon')}
            value={iconFile}
            onChange={setIconFile}
            existingUrl={isEditing ? editingItem?.iconUrl : null}
            disabled={isPending}
            maxSize={MAX_ICON_SIZE}
            maxSizeMessage={t('skills.iconTooLarge')}
          />
          <FormSwitch control={control} name="isActive" label={t('common.active')} />
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => closeDialog(dialogId)} disabled={isPending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={isPending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
